import { useState, useEffect, useRef } from 'react';
import { Save, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getSettings, updateSettings, uploadFile } from '@/lib/api';

export const SettingsManager = () => {
  const [settings, setSettings] = useState({
    name: '',
    tagline: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    hours: '',
    logo_url: '',
    hero_image_url: '',
    about_text: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Logo upload state
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  
  // Hero image upload state
  const [selectedHeroImage, setSelectedHeroImage] = useState(null);
  const [heroImagePreview, setHeroImagePreview] = useState(null);
  const heroImageInputRef = useRef(null);

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getSettings();
        setSettings(response.data);
        if (response.data.logo_url) {
          setLogoPreview(response.data.logo_url);
        }
        if (response.data.hero_image_url) {
          setHeroImagePreview(response.data.hero_image_url);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Wybierz plik obrazu');
        return;
      }
      setSelectedLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleHeroImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Wybierz plik obrazu');
        return;
      }
      setSelectedHeroImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setHeroImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      let updatedSettings = { ...settings };

      // Upload logo if new one selected
      if (selectedLogo) {
        const formData = new FormData();
        formData.append('file', selectedLogo);
        const uploadRes = await uploadFile(formData);
        updatedSettings.logo_url = uploadRes.data.url;
      }

      // Upload hero image if new one selected
      if (selectedHeroImage) {
        const formData = new FormData();
        formData.append('file', selectedHeroImage);
        const uploadRes = await uploadFile(formData);
        updatedSettings.hero_image_url = uploadRes.data.url;
      }

      await updateSettings(updatedSettings);
      setSettings(updatedSettings);
      setSelectedLogo(null);
      setSelectedHeroImage(null);
      toast.success('Ustawienia zapisane');
    } catch (error) {
      toast.error('Wystąpił błąd');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="settings-manager">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Ustawienia</h1>
        <p className="text-slate-400">Zarządzaj informacjami o firmie</p>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl">
        <div className="bg-[#111827] rounded-xl border border-cyan-500/10 p-6 space-y-6">
          <h2 className="text-lg font-semibold text-white pb-4 border-b border-cyan-500/10">
            Informacje podstawowe
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Nazwa firmy
              </label>
              <Input
                value={settings.name}
                onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                placeholder="CAM VISION"
                className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
                data-testid="settings-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Hasło
              </label>
              <Input
                value={settings.tagline}
                onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                placeholder="Profesjonalne Systemy Bezpieczeństwa"
                className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
                data-testid="settings-tagline-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Opis firmy
            </label>
            <Textarea
              value={settings.description}
              onChange={(e) => setSettings({ ...settings, description: e.target.value })}
              rows={2}
              placeholder="Krótki opis działalności..."
              className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
              data-testid="settings-description-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Logo firmy
            </label>
            <div 
              className="border-2 border-dashed border-cyan-500/20 rounded-lg p-4 text-center cursor-pointer hover:border-cyan-500/40 transition-colors bg-[#0A0F1C]"
              onClick={() => logoInputRef.current?.click()}
            >
              {logoPreview ? (
                <div className="relative inline-block">
                  <img
                    src={logoPreview.startsWith('data:') ? logoPreview : getImageUrl(logoPreview)}
                    alt="Logo preview"
                    className="h-20 w-auto mx-auto"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLogo(null);
                      setLogoPreview(null);
                      setSettings({ ...settings, logo_url: '' });
                    }}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <p className="text-sm text-slate-400 mt-2">
                    Kliknij aby zmienić logo
                  </p>
                </div>
              ) : (
                <div className="py-4">
                  <Upload className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                  <p className="text-slate-300">Kliknij aby wybrać logo</p>
                  <p className="text-xs text-slate-500 mt-1">PNG, JPG, WEBP (zalecane: przezroczyste tło)</p>
                </div>
              )}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              className="hidden"
              data-testid="settings-logo-input"
            />
          </div>
        </div>

        {/* Hero Image Section */}
        <div className="bg-[#111827] rounded-xl border border-cyan-500/10 p-6 space-y-6 mt-6">
          <h2 className="text-lg font-semibold text-white pb-4 border-b border-cyan-500/10">
            Zdjęcie główne (Hero)
          </h2>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Zdjęcie wyświetlane na górze strony głównej
            </label>
            <div 
              className="border-2 border-dashed border-cyan-500/20 rounded-lg p-4 text-center cursor-pointer hover:border-cyan-500/40 transition-colors bg-[#0A0F1C]"
              onClick={() => heroImageInputRef.current?.click()}
            >
              {heroImagePreview ? (
                <div className="relative">
                  <img
                    src={heroImagePreview.startsWith('data:') ? heroImagePreview : getImageUrl(heroImagePreview)}
                    alt="Hero preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedHeroImage(null);
                      setHeroImagePreview(null);
                      setSettings({ ...settings, hero_image_url: '' });
                    }}
                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <p className="text-sm text-slate-400 mt-2">
                    Kliknij aby zmienić zdjęcie główne
                  </p>
                </div>
              ) : (
                <div className="py-8">
                  <Upload className="w-10 h-10 text-cyan-400 mx-auto mb-2" />
                  <p className="text-slate-300">Kliknij aby wybrać zdjęcie główne</p>
                  <p className="text-xs text-slate-500 mt-1">JPG, PNG, WEBP (zalecane: 1920x1080 lub większe)</p>
                </div>
              )}
            </div>
            <input
              ref={heroImageInputRef}
              type="file"
              accept="image/*"
              onChange={handleHeroImageChange}
              className="hidden"
              data-testid="settings-hero-image-input"
            />
          </div>
        </div>

        <div className="bg-[#111827] rounded-xl border border-cyan-500/10 p-6 space-y-6 mt-6">
          <h2 className="text-lg font-semibold text-white pb-4 border-b border-cyan-500/10">
            Dane kontaktowe
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Telefon
              </label>
              <Input
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                placeholder="+48 530 500 047"
                className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
                data-testid="settings-phone-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email
              </label>
              <Input
                type="email"
                value={settings.email}
                onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                placeholder="kontakt@camvision.pl"
                className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
                data-testid="settings-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Adres
            </label>
            <Input
              value={settings.address}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              placeholder="Storczykowa, 05-500 Zgorzała"
              className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
              data-testid="settings-address-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Godziny pracy
            </label>
            <Input
              value={settings.hours}
              onChange={(e) => setSettings({ ...settings, hours: e.target.value })}
              placeholder="Pon-Sob 8:00 - 17:00"
              className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
              data-testid="settings-hours-input"
            />
          </div>
        </div>

        <div className="bg-[#111827] rounded-xl border border-cyan-500/10 p-6 space-y-6 mt-6">
          <h2 className="text-lg font-semibold text-white pb-4 border-b border-cyan-500/10">
            Sekcja "O nas"
          </h2>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Tekst o firmie
            </label>
            <Textarea
              value={settings.about_text}
              onChange={(e) => setSettings({ ...settings, about_text: e.target.value })}
              rows={4}
              placeholder="Opis firmy dla sekcji O nas..."
              className="bg-[#0A0F1C] border-cyan-500/20 text-white placeholder:text-slate-500"
              data-testid="settings-about-input"
            />
          </div>
        </div>

        <div className="mt-6">
          <Button
            type="submit"
            disabled={saving}
            className="h-12 px-8 rounded-full bg-gradient-to-r from-cyan-400 to-cyan-600 text-[#0A0F1C] hover:from-cyan-300 hover:to-cyan-500 font-semibold shadow-lg shadow-cyan-500/30"
            data-testid="settings-save-button"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Zapisywanie...' : 'Zapisz ustawienia'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SettingsManager;
